<script setup lang="ts">
import {toRef} from 'vue'
import * as d3 from 'd3'
import type {ChartData, ChartSeries} from '@/client/mentor/MessagesClient'
import AssistantMessageChartTooltip from '@/components/assistant/chat/message/AssistantMessageChartTooltip.vue'
import {useAssistantMessageChartLifecycle} from '@/components/assistant/composables/useAssistantMessageChartLifecycle'
import {useAssistantMessageChart} from '@/components/assistant/composables/useAssistantMessageChart'

interface Props {
  chartData: ChartData
}

const props = defineProps<Props>()
const chartData = toRef(props, 'chartData')

const {
  chartTheme,
  chartContainer,
  hiddenLegendItems,
  hoverTooltip,
  cartesianLegendItems,
  allSeries,
  visibleSeries,
  allCategories,
  attachTooltipHandlers,
  hideTooltip,
  formatTooltipValue,
  formatXAxisLabel,
  toNumericValue,
  getXAxisTickValues,
  shouldRotateXAxisTickLabels,
  getXAxisBottomMargin,
  getLegendHeight,
  renderLegendInSvg,
} = useAssistantMessageChart(chartData)

useAssistantMessageChartLifecycle(chartContainer, chartData, hiddenLegendItems, drawChart)

function drawChart() {
  if (!chartContainer.value) {
    return
  }

  const container = chartContainer.value
  const width = container.clientWidth || 320
  const defaultMargin = {top: 32, right: 24, bottom: 40, left: 72}
  const categories = allCategories.value

  if (!categories.length) {
    d3.select(container).selectAll('*').remove()
    hideTooltip()
    return
  }

  const initialPlotWidth = width - defaultMargin.left - defaultMargin.right
  const xAxisTicks = getXAxisTickValues(categories, initialPlotWidth)
  const shouldRotateXAxisLabels = shouldRotateXAxisTickLabels(xAxisTicks.formattedLabels, initialPlotWidth)
  const margin = {
    ...defaultMargin,
    bottom: getXAxisBottomMargin(xAxisTicks.formattedLabels, shouldRotateXAxisLabels, defaultMargin.bottom),
  }

  const chartHeight = 220
  const legendHeight = getLegendHeight(cartesianLegendItems.value, width - margin.left - margin.right)
  const height = chartHeight + margin.top + margin.bottom + legendHeight

  d3.select(container).selectAll('*').remove()
  hideTooltip()

  const svg = d3
    .select(container)
    .append('svg')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('class', 'overflow-visible')

  const plotWidth = width - margin.left - margin.right
  const plotHeight = chartHeight
  const plot = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`)
  const colorScale = d3
    .scaleOrdinal<string>()
    .domain(cartesianLegendItems.value.map((item) => item.key))
    .range(cartesianLegendItems.value.map((item) => item.color))

  const numericValues = visibleSeries.value.flatMap((series) =>
    (series.points ?? []).flatMap((point) => {
      const value = toNumericValue(point.y)
      return value === null ? [] : [value]
    })
  )
  const maxValue = d3.max(numericValues) ?? 0

  const xScale = d3.scaleBand<string>().domain(categories).range([0, plotWidth]).padding(0.24)
  const yScale = d3
    .scaleLinear()
    .domain([0, maxValue || 1])
    .nice()
    .range([plotHeight, 0])

  plot
    .append('g')
    .attr('transform', `translate(0, ${plotHeight})`)
    .call(
      d3
        .axisBottom(xScale)
        .tickValues(xAxisTicks.values)
        .tickFormat((value) => formatXAxisLabel(String(value)))
    )
    .call((axis) => {
      const text = axis.selectAll('text').attr('fill', chartTheme.axisTextColor).attr('font-size', 11)

      if (shouldRotateXAxisLabels) {
        text.attr('text-anchor', 'end').attr('transform', 'rotate(-45)')
      }
    })
    .call((axis) => axis.selectAll('path,line').attr('stroke', chartTheme.axisStrokeColor))

  const yAxis = plot
    .append('g')
    .call(
      d3
        .axisLeft(yScale)
        .ticks(5)
        .tickFormat((value) => {
          const numericValue = typeof value === 'number' ? value : Number(value)
          return `${numericValue}`
        })
    )
    .call((axis) => axis.selectAll('text').attr('fill', chartTheme.axisTextColor).attr('font-size', 11))
    .call((axis) => axis.selectAll('path,line').attr('stroke', chartTheme.axisStrokeColor))

  plot
    .append('g')
    .selectAll('line')
    .data(yScale.ticks(5))
    .join('line')
    .attr('x1', 0)
    .attr('x2', plotWidth)
    .attr('y1', (tick) => yScale(tick))
    .attr('y2', (tick) => yScale(tick))
    .attr('stroke', chartTheme.gridStrokeColor)
    .attr('stroke-dasharray', '2 4')

  drawLines(plot, xScale, yScale, plotWidth, visibleSeries.value, colorScale)

  if (chartData.value.xLabel) {
    svg
      .append('text')
      .attr('x', margin.left + plotWidth / 2)
      .attr('y', height - legendHeight - (shouldRotateXAxisLabels ? 8 : 12))
      .attr('text-anchor', 'middle')
      .attr('fill', chartTheme.axisLabelColor)
      .attr('font-size', 11)
      .text(chartData.value.xLabel)
  }

  if (chartData.value.yLabel) {
    const yAxisNode = yAxis.node()
    const yAxisBounds = yAxisNode?.getBBox()
    const yAxisLabelX = yAxisBounds ? Math.max(8, margin.left + yAxisBounds.x - 16) : Math.max(8, margin.left - 56)

    svg
      .append('text')
      .attr('transform', `translate(${yAxisLabelX}, ${margin.top + plotHeight / 2}) rotate(-90)`)
      .attr('text-anchor', 'middle')
      .attr('fill', chartTheme.axisLabelColor)
      .attr('font-size', 11)
      .text(chartData.value.yLabel)
  }

  renderLegendInSvg(svg, cartesianLegendItems.value, margin, 'cartesian', shouldRotateXAxisLabels, legendHeight)
}

function drawLines(
  plot: d3.Selection<SVGGElement, unknown, null, undefined>,
  xScale: d3.ScaleBand<string>,
  yScale: d3.ScaleLinear<number, number>,
  plotWidth: number,
  visible: ChartSeries[],
  colorScale: d3.ScaleOrdinal<string, string, never>
) {
  const lineGenerator = d3
    .line<{x: string; y: number | null}>()
    .defined((point) => point.y !== null)
    .x((point) => (xScale(point.x) ?? 0) + xScale.bandwidth() / 2)
    .y((point) => yScale(point.y ?? 0))
    .curve(d3.curveMonotoneX)

  const hasMultipleSeries = allSeries.value.length > 1

  for (const series of visible) {
    const valueByCategory = new Map((series.points ?? []).map((point) => [String(point.x), toNumericValue(point.y)]))
    const seriesData = allCategories.value.map((category) => ({x: category, y: valueByCategory.get(category) ?? null}))
    const visiblePoints = seriesData.filter((point): point is {x: string; y: number} => point.y !== null)

    if (!seriesData.length) {
      continue
    }

    const lineColor = colorScale(series.name)

    plot
      .append('path')
      .datum(seriesData)
      .attr('fill', 'none')
      .attr('stroke', lineColor)
      .attr('stroke-width', 2.5)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('d', lineGenerator)

    const points = plot
      .append('g')
      .attr('fill', lineColor)
      .selectAll('circle')
      .data(visiblePoints)
      .join('circle')
      .attr('cx', (point) => (xScale(point.x) ?? 0) + xScale.bandwidth() / 2)
      .attr('cy', (point) => yScale(point.y))
      .attr('r', plotWidth < 420 ? 2.5 : 3.5)

    attachTooltipHandlers(points, (point: {x: string; y: number}) => {
      const title = hasMultipleSeries ? `${series.name} - ${formatXAxisLabel(point.x)}` : formatXAxisLabel(point.x)
      return {
        title,
        value: formatTooltipValue(point.y, series.unit),
      }
    })
  }
}
</script>

<template>
  <div class="relative w-full rounded-xl border" :class="chartTheme.containerClass">
    <div ref="chartContainer" class="w-full overflow-hidden"></div>

    <AssistantMessageChartTooltip
      :container-class="chartTheme.tooltipContainerClass"
      :title-class="chartTheme.tooltipTitleClass"
      :tooltip="hoverTooltip"
      :value-class="chartTheme.tooltipValueClass"
    />
  </div>
</template>
